'use client';

import { useState, useEffect, useMemo, useCallback } from 'react';
import { usePathname } from 'next/navigation';
import Link from 'next/link';
import { PanelLeft } from 'lucide-react';

import { useSidebarCollapsed } from '@/hooks/useSidebarCollapsed';
import { useSidebarSectionsStore } from '@/state/sidebarSectionsState';
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from '@/components/ui/collapsible';
import Image from 'next/image';

import {
  House,
  GitPullRequest,
  FileText,
  Trophy,
  BookOpen,
  AsteriskSimple,
  ChatCircle,
  Atom,
  Pulse,
  CaretDown,
  Lightning,
  Warning,
  Gear,
  UsersThree,
  SquaresFour,
  Sparkle,
  Code,
  CloudFog,
  Path,
  BracketsCurly,
  ShieldCheck,
  TerminalWindow,
  Graph,
  ChartBar,
} from '@phosphor-icons/react';
import { SidebarPromoCard } from '@/components/Sidebar/SidebarPromoCard';
import { WorkspaceMenu } from '@/components/Sidebar/WorkspaceMenu';
import { cn } from '@/lib/utils';
import { isOwner, isAdminOrOwner } from '@/lib/rbac';
import { useUserLogged } from '@/hooks/useUserLogged';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { useTrialAccessStatus } from '@/hooks/useTrialAccess';
import { LEADERBOARD_HIDDEN_FLAG } from '@/apis/featureFlags';
import useSideBarState from '@/state/sideBarState';

// SHADCN Tooltip import
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { ManagerChatSection } from '@/components/Sidebar/ManagerChatSection';
import type { NavigationConfigItem } from '@/lib/navigationConfig.types';
import { useIntercomState } from '@/state/intercomState';
import { useAskEllieSidebarStore } from '@/state/askEllieSidebarState';
import { isModelQualityEnabled } from '@/lib/featureFlags/modelQuality';
import { isReactorsOrg } from '@/types/reactorTypes';

enum SidebarItemType {
  Home = 'home',
  Chat = 'dashboard',
  Assistant = 'ask-ellie',
  WorldModel = 'world-model',
  HowToUse = 'how-to-use',
  BookACall = 'book-a-call',
  ContactUs = 'contact-us',
  CliGuide = 'cli',
  CodeReview = 'code-review',
  Documentation = 'documentation',
  IncidentKnowledge = 'incident-knowledge',
  Connectors = 'connectors',
  TeamInsight = 'team-insight',
  AgentInsights = 'agent-insights',
  ModelRouter = 'model-router',
  ModelQuality = 'model-quality',
  CodeQuality = 'code-quality',
  DeploymentHealth = 'deployment-health',
  IndividualInsights = 'individual-insights',
  Boards = 'boards',
  Leaderboard = 'leaderboard',
  Analytics = 'analytics',
  BackgroundAgents = 'background-agents',
  Pulse = 'pulse',
  Reactors = 'reactors',
  Automations = 'automations',
  WorkspaceSettings = 'settings',
  Livesite = 'livesite',
  ObservabilityAudit = 'observability-audit',
  CodeReliability = 'code-reliability',
  Security = 'security',
}

interface SectionType {
  heading: string;
  type: SidebarItemType;
  icon: React.ReactElement;
  group?: string;
  uuid?: string;
  role?: 'admin' | 'member' | 'owner';
  badge?: string;
}

interface SidebarProps {
  className?: string;
  /** Cached nav config from server (cacheComponents); when provided, sections are built from this plus client user data */
  navConfig?: NavigationConfigItem[];
}

const SIDEBAR_ICON_MAP: Record<string, React.ReactElement> = {
  home: <House size={16} weight="regular" />,
  connectors: <Lightning size={16} weight="regular" />,
  'code-review': <GitPullRequest size={16} weight="regular" />,
  documentation: <FileText size={16} weight="regular" />,
  'incident-knowledge': <Atom size={16} weight="regular" />,
  livesite: <Warning size={16} weight="regular" />,
  'observability-audit': <BracketsCurly size={16} weight="regular" />,
  'code-reliability': <ShieldCheck size={16} weight="regular" />,
  security: <ShieldCheck size={16} weight="regular" />,
  'team-insight': <UsersThree size={16} weight="regular" />,
  'agent-insights': <Sparkle size={16} weight="regular" />,
  'model-router': <Path size={16} weight="regular" />,
  'model-quality': <ChartBar size={16} weight="regular" />,
  'code-quality': <Code size={16} weight="regular" />,
  'deployment-health': <CloudFog size={16} weight="regular" />,
  'individual-insights': <UsersThree size={16} weight="regular" />,
  boards: <SquaresFour size={16} weight="regular" />,
  leaderboard: <Trophy size={16} weight="regular" />,
  pulse: <Pulse size={16} weight="regular" />,
  reactors: <Lightning size={16} weight="regular" />,
  automations: <Pulse size={16} weight="regular" />,
  'how-to-use': <BookOpen size={16} weight="regular" />,
  'book-a-call': <AsteriskSimple size={16} weight="regular" />,
  'contact-us': <ChatCircle size={16} weight="regular" />,
  cli: <TerminalWindow size={16} weight="regular" />,
  settings: <Gear size={16} weight="regular" />,
};

// ponytail: badges removed on request. Re-add an entry here to bring one back.
const SECTION_BADGE_OVERRIDES: Partial<Record<SidebarItemType, string>> = {};

const CONNECTORS_SECTION: SectionType = {
  heading: 'Connectors',
  type: SidebarItemType.Connectors,
  icon: <Lightning size={16} weight="regular" />,
};

// World Model = read-only memory-graph explorer. Rendered as a standalone
// top-level item next to Ask Ellie. Admin-only (same rule as Agent Insights):
// person nodes and neighbourhoods expose a person's sessions, so a member
// browsing the graph could reach a teammate's data the same way the Agent
// Insights leak did.
const WORLD_MODEL_SECTION: SectionType = {
  heading: 'World Model',
  type: SidebarItemType.WorldModel,
  icon: <Graph size={16} weight="regular" />,
};

const BOOK_A_CALL_SECTION: SectionType = {
  heading: 'Talk to Expert',
  type: SidebarItemType.BookACall,
  icon: <AsteriskSimple size={16} weight="regular" />,
  group: 'SUPPORT',
};

const CONTACT_US_SECTION: SectionType = {
  heading: 'Message Support',
  type: SidebarItemType.ContactUs,
  icon: <ChatCircle size={16} weight="regular" />,
  group: 'SUPPORT',
};

// Canonical order — applied to both navConfig-driven and createNavigationSections outputs
// so the rendered sidebar always matches Figma regardless of upstream ordering.
const SECTION_ORDER: SidebarItemType[] = [
  SidebarItemType.Home,
  // CODE
  SidebarItemType.CodeReview,
  SidebarItemType.CodeReliability,
  SidebarItemType.Livesite,
  SidebarItemType.Security,
  SidebarItemType.Documentation,
  // AGENTS
  SidebarItemType.AgentInsights,
  SidebarItemType.ModelRouter,
  SidebarItemType.ModelQuality,
  // TEAM
  SidebarItemType.TeamInsight,
  SidebarItemType.Leaderboard,
  SidebarItemType.Automations,
  // DELIVERY
  SidebarItemType.Boards,
  // SUPPORT
  SidebarItemType.HowToUse,
  SidebarItemType.CliGuide,
  SidebarItemType.BookACall,
  SidebarItemType.ContactUs,
  SidebarItemType.WorkspaceSettings,
];

function sortByCanonicalOrder(sections: SectionType[]): SectionType[] {
  const indexOf = (t: SidebarItemType) => {
    const i = SECTION_ORDER.indexOf(t);
    return i === -1 ? SECTION_ORDER.length : i;
  };
  return [...sections].sort((a, b) => indexOf(a.type) - indexOf(b.type));
}

function sectionsFromNavConfig(
  navConfig: NavigationConfigItem[],
  userTeamUUID?: string
): SectionType[] {
  return navConfig.map((item) => ({
    heading: item.heading,
    type: item.type as SidebarItemType,
    icon: SIDEBAR_ICON_MAP[item.type] ?? <House size={16} weight="regular" />,
    group: item.group,
    uuid: item.type === 'team-insight' ? userTeamUUID : undefined,
    role: item.role,
    badge: SECTION_BADGE_OVERRIDES[item.type as SidebarItemType],
  }));
}

// Helper function to create navigation sections
const createNavigationSections = (userTeamUUID?: string): SectionType[] => [
  // General sections (no group — always visible at top)
  {
    heading: 'Home',
    type: SidebarItemType.Home,
    icon: <House size={16} weight="regular" />,
  },
  // Code section
  {
    heading: 'Code Review',
    type: SidebarItemType.CodeReview,
    icon: <GitPullRequest size={16} weight="regular" />,
    group: 'CODE',
  },
  {
    heading: 'Code Reliability',
    type: SidebarItemType.CodeReliability,
    icon: <ShieldCheck size={16} weight="regular" />,
    group: 'CODE',
  },
  {
    heading: 'Livesite',
    type: SidebarItemType.Livesite,
    icon: <Warning size={16} weight="regular" />,
    group: 'CODE',
  },
  // Code Security hidden for now — route remains at /security.
  {
    heading: 'Knowledge Base',
    type: SidebarItemType.Documentation,
    icon: <FileText size={16} weight="regular" />,
    group: 'CODE',
  },
  // Team section
  {
    heading: 'Agent Insights',
    type: SidebarItemType.AgentInsights,
    icon: <Sparkle size={16} weight="regular" />,
    group: 'AGENTS',
    role: 'admin',
  },
  {
    heading: 'Model Router',
    type: SidebarItemType.ModelRouter,
    icon: <Path size={16} weight="regular" />,
    group: 'AGENTS',
  },
  {
    heading: 'Team Insights',
    type: SidebarItemType.TeamInsight,
    icon: <UsersThree size={16} weight="regular" />,
    group: 'TEAM',
    uuid: userTeamUUID,
    role: 'admin',
  },
  // Individual Insights moved into Team Insights as a tab (?view=individual).
  {
    heading: 'Boards',
    type: SidebarItemType.Boards,
    icon: <SquaresFour size={16} weight="regular" />,
    group: 'WORKFLOW',
  },
  {
    heading: 'Automations',
    type: SidebarItemType.Automations,
    icon: <Pulse size={16} weight="regular" />,
    group: 'TEAM',
    role: 'admin',
  },
  {
    heading: 'Leaderboard',
    type: SidebarItemType.Leaderboard,
    icon: <Trophy size={16} weight="regular" />,
    group: 'TEAM',
  },
  // Support section
  {
    heading: 'How to Use',
    type: SidebarItemType.HowToUse,
    icon: <BookOpen size={16} weight="regular" />,
    group: 'SUPPORT',
  },
  {
    heading: 'CLI Guide',
    type: SidebarItemType.CliGuide,
    icon: <TerminalWindow size={16} weight="regular" />,
    group: 'SUPPORT',
  },
  {
    heading: 'Talk to Expert',
    type: SidebarItemType.BookACall,
    icon: <AsteriskSimple size={16} weight="regular" />,
    group: 'SUPPORT',
  },
  {
    heading: 'Message Support',
    type: SidebarItemType.ContactUs,
    icon: <ChatCircle size={16} weight="regular" />,
    group: 'SUPPORT',
  },
  {
    heading: 'Workspace Settings',
    type: SidebarItemType.WorkspaceSettings,
    icon: <Gear size={16} weight="regular" />,
    group: 'SUPPORT',
  },
];

// Helper function to filter sections based on user role
const filterSectionsByRole = (
  sections: SectionType[],
  userRole?: string
): SectionType[] => {
  return sections.filter((section) => {
    if (!section.role) return true;
    if (section.role === 'admin') return isAdminOrOwner(userRole);
    if (section.role === 'owner') return isOwner(userRole);
    return section.role === userRole;
  });
};

// Helper function to determine active section from pathname
const getActiveSectionFromPath = (pathname: string): SidebarItemType | null => {
  const pathMappings: Record<string, SidebarItemType> = {
    '/home': SidebarItemType.Home,
    '/documentation': SidebarItemType.Documentation,
    '/code-reliability': SidebarItemType.CodeReliability,
    '/livesite': SidebarItemType.Livesite,
    '/security': SidebarItemType.Security,
    '/code-review': SidebarItemType.CodeReview,
    '/ask-ellie': SidebarItemType.Assistant,
    '/settings': SidebarItemType.WorkspaceSettings,
  };

  if (pathMappings[pathname]) {
    return pathMappings[pathname];
  }

  const pathSegment = pathname.split('/')[1] as SidebarItemType;
  // Incident Knowledge + Observability were merged into Code Reliability; their
  // legacy base routes and repo deep-links highlight the combined nav item.
  if (
    pathSegment === SidebarItemType.IncidentKnowledge ||
    pathSegment === SidebarItemType.ObservabilityAudit ||
    pathSegment === SidebarItemType.CodeReliability
  ) {
    return SidebarItemType.CodeReliability;
  }
  return Object.values(SidebarItemType).includes(pathSegment)
    ? pathSegment
    : null;
};

// Helper function to get href for a section (returns null for special cases that need onClick)
const getSectionHref = (
  section: SectionType,
  userOrgUUID?: string
): string | null => {
  switch (section.type) {
    case SidebarItemType.Chat:
      // Chat uses onClick to toggle history
      return null;
    case SidebarItemType.HowToUse:
      // External link, handled by onClick
      return null;
    case SidebarItemType.BookACall:
      // External link, handled by onClick
      return null;
    case SidebarItemType.ContactUs:
      return null;
    case SidebarItemType.TeamInsight:
      return '/team-insight';
    case SidebarItemType.Leaderboard:
      return userOrgUUID ? `/leaderboard/${userOrgUUID}` : null;
    case SidebarItemType.Pulse:
      return '/automations';
    case SidebarItemType.Reactors:
      return '/reactors';
    case SidebarItemType.WorkspaceSettings:
      return '/settings';
    default:
      return `/${section.type}`;
  }
};

// Helper function to handle section navigation (for special cases only)
const handleSectionNavigation = async (
  section: SectionType,
  toggleHistory: () => void,
  onSectionChange?: (type: SidebarItemType) => void
) => {
  switch (section.type) {
    case SidebarItemType.Chat:
      // TODO: we probably want the page to pop up like history in claude
      toggleHistory();
      break;
    case SidebarItemType.HowToUse:
      window.open('https://docs.entelligence.ai', '_blank');
      break;
    case SidebarItemType.BookACall:
      window.open('https://calendly.com/aiswarya-s/30min', '_blank');
      break;
    case SidebarItemType.ContactUs:
      if (typeof window !== 'undefined' && window.Intercom) {
        const intercomState = useIntercomState.getState();
        if (intercomState.isOpen) {
          // Toggle off: clicking the button while Intercom is open closes it.
          window.Intercom('hide');
          intercomState.setIsOpen(false);
          onSectionChange?.(null as unknown as SidebarItemType);
        } else {
          window.Intercom('show');
          intercomState.setIsOpen(true);
          // Chain both side effects in onHide: keep the zustand `isOpen`
          // in sync AND clear the sidebar's active highlight when the user
          // closes Intercom by any means (X button, esc, click-outside).
          window.Intercom('onHide', () => {
            useIntercomState.getState().setIsOpen(false);
            onSectionChange?.(null as unknown as SidebarItemType);
          });
          onSectionChange?.(SidebarItemType.ContactUs);
        }
      }
      break;
    default:
      onSectionChange?.(section.type);
      break;
  }
};

// Helper function to group sections by group type
const groupSectionsByType = (sections: SectionType[]) => {
  return {
    general: sections.filter((section) => !section.group),
    code: sections.filter((section) => section.group === 'CODE'),
    agents: sections.filter((section) => section.group === 'AGENTS'),
    team: sections.filter((section) => section.group === 'TEAM'),
    workflow: sections.filter((section) => section.group === 'WORKFLOW'),
    support: sections.filter((section) => section.group === 'SUPPORT'),
  };
};

interface SidebarSectionButtonProps {
  section: SectionType;
  organizationUUID: string | undefined;
  sectionOpened: SidebarItemType | null;
  isCollapsed: boolean;
  onSectionClick: (section: SectionType) => void;
  onSetSectionOpened: (type: SidebarItemType) => void;
}

function SidebarSectionButton({
  section,
  organizationUUID,
  sectionOpened,
  isCollapsed,
  onSectionClick,
  onSetSectionOpened,
}: SidebarSectionButtonProps) {
  const href = getSectionHref(section, organizationUUID);
  const needsOnClick = href === null;

  const isActive = sectionOpened === section.type;

  const itemInner = (isLink: boolean) => (
    <div
      className={cn(
        'flex items-center w-full h-9 overflow-hidden rounded-lg transition-colors duration-150',
        !isCollapsed &&
          (isActive
            ? 'bg-ds-brand-lightest border border-ds-brand-subtlest'
            : 'hover:bg-ds-gray-subtlest border border-transparent')
      )}
    >
      {/* Fixed icon column — anchors icon at same X position in both states */}
      <div className="flex items-center justify-center shrink-0 w-12 h-9">
        <div
          className={cn(
            'flex items-center justify-center size-9 rounded-lg transition-colors duration-150',
            isCollapsed &&
              isActive &&
              'bg-ds-brand-lightest border border-ds-brand-subtlest',
            isCollapsed && !isActive && 'hover:bg-ds-gray-subtlest',
            isActive ? 'text-ds-brand-default' : 'text-ds-text-subtle'
          )}
        >
          {section.icon}
        </div>
      </div>
      <div
        className={cn(
          'flex items-center gap-2 min-w-0 whitespace-nowrap overflow-hidden transition-[max-width,opacity] duration-200 ease-out',
          isCollapsed ? 'max-w-0 opacity-0' : 'max-w-[180px] opacity-100 pr-2'
        )}
      >
        <span
          className={cn(
            'font-[500] leading-[18px] font-sf-pro-text text-sm truncate',
            isActive ? 'text-ds-brand-default' : 'text-ds-text-subtle'
          )}
        >
          {section.heading}
        </span>
        {section.badge && (
          <span className="shrink-0 text-[12px] leading-[1.3] font-medium px-[9px] py-[3px] rounded bg-ds-brand-lightest text-ds-brand-default border border-ds-brand-subtlest">
            {section.badge}
          </span>
        )}
      </div>
    </div>
  );

  const buttonContent = (
    <div
      role="button"
      aria-disabled={!needsOnClick}
      tabIndex={needsOnClick ? 0 : undefined}
      onClick={needsOnClick ? () => onSectionClick(section) : undefined}
      onKeyDown={
        needsOnClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSectionClick(section);
              }
            }
          : undefined
      }
      className={cn(
        'flex items-center transition-colors duration-100',
        needsOnClick && 'cursor-pointer',
        isCollapsed ? 'justify-center' : 'justify-start'
      )}
      aria-label={isCollapsed ? section.heading : undefined}
    >
      {href ? (
        <Link
          href={href}
          onClick={() => onSetSectionOpened(section.type)}
          className="w-full"
          aria-label={isCollapsed ? section.heading : undefined}
        >
          {itemInner(true)}
        </Link>
      ) : (
        <div
          role="button"
          tabIndex={0}
          onClick={() => onSectionClick(section)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onSectionClick(section);
            }
          }}
          className="w-full cursor-pointer"
          aria-label={isCollapsed ? section.heading : undefined}
        >
          {itemInner(false)}
        </div>
      )}
    </div>
  );

  if (isCollapsed) {
    return (
      <Tooltip>
        <TooltipTrigger delay={200} render={buttonContent} />
        <TooltipContent side="right" align="center">
          {section.heading}
        </TooltipContent>
      </Tooltip>
    );
  }
  return buttonContent;
}

interface SidebarSectionGroupProps {
  sections: SectionType[];
  groupName: string;
  isCollapsed: boolean;
  sectionOpened: SidebarItemType | null;
  organizationUUID: string | undefined;
  onSectionClick: (section: SectionType) => void;
  onSetSectionOpened: (type: SidebarItemType) => void;
}

function SidebarSectionGroup({
  sections,
  groupName,
  isCollapsed,
  sectionOpened,
  organizationUUID,
  onSectionClick,
  onSetSectionOpened,
}: SidebarSectionGroupProps) {
  // Per-group expand/collapse, persisted across reloads via the Zustand store.
  // Defaults to expanded (absent key === not collapsed).
  const collapsed = useSidebarSectionsStore(
    (state) => state.collapsed[groupName] ?? false
  );
  const toggleSection = useSidebarSectionsStore((state) => state.toggleSection);

  if (sections.length === 0) return null;

  // When the whole sidebar is icon-collapsed the group header is hidden, so the
  // per-group toggle doesn't apply — force the group open so items always show.
  const open = isCollapsed ? true : !collapsed;

  return (
    <Collapsible
      open={open}
      onOpenChange={isCollapsed ? undefined : () => toggleSection(groupName)}
    >
      <CollapsibleTrigger
        disabled={isCollapsed}
        render={
          <button
            type="button"
            className={cn(
              'relative pl-4 pr-3 flex items-center justify-between w-full overflow-hidden',
              isCollapsed ? 'py-3' : 'mt-3 h-7 cursor-pointer group/section'
            )}
          >
            {/* Separator — always mounted, fades in when collapsed so it never jumps */}
            <div
              aria-hidden
              className={cn(
                'absolute left-1/2 -translate-x-1/2 w-5 h-px bg-ds-border-subtlest rounded-full transition-opacity duration-200',
                isCollapsed ? 'opacity-100' : 'opacity-0'
              )}
            />
            <span
              className={cn(
                'font-medium text-ds-text-subtle text-[12px] leading-[1.3] whitespace-nowrap overflow-hidden transition-[max-width,opacity,color] duration-200 ease-out group-hover/section:text-ds-text-default',
                isCollapsed ? 'max-w-0 opacity-0' : 'max-w-[180px] opacity-100'
              )}
            >
              {groupName}
            </span>
            {!isCollapsed && (
              <CaretDown
                size={12}
                weight="bold"
                className={cn(
                  'shrink-0 text-ds-text-subtle transition-transform duration-200 group-hover/section:text-ds-text-default',
                  collapsed && '-rotate-90'
                )}
                aria-hidden
              />
            )}
          </button>
        }
      />
      <CollapsibleContent>
        <div className="space-y-0.5">
          {sections.map((section) => (
            <SidebarSectionButton
              key={section.type}
              section={section}
              organizationUUID={organizationUUID}
              sectionOpened={sectionOpened}
              isCollapsed={isCollapsed}
              onSectionClick={onSectionClick}
              onSetSectionOpened={onSetSectionOpened}
            />
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

const SCROLL_HINT_KEY = 'sidebar-scroll-hint-seen';

/**
 * Isolated intercom subscription — keeps re-renders scoped to this button
 * instead of bubbling intercom state changes to the entire Sidebar tree.
 */
function MessageSupportButton({
  isCollapsed,
  onClick,
}: {
  isCollapsed: boolean;
  onClick: () => void;
}) {
  const { isOpen: intercomOpen, unreadCount } = useIntercomState();
  return (
    <button
      type="button"
      aria-label={isCollapsed ? 'Message Support' : undefined}
      onClick={onClick}
      className={cn(
        'flex items-center w-full h-9 rounded-lg overflow-hidden border transition-colors duration-150',
        !isCollapsed && intercomOpen
          ? 'bg-ds-brand-lightest border-ds-brand-subtlest'
          : 'border-transparent',
        !isCollapsed && !intercomOpen && 'hover:bg-ds-gray-subtlest'
      )}
    >
      <div className="flex items-center justify-center shrink-0 w-12 h-9">
        <div
          className={cn(
            'flex items-center justify-center size-9 rounded-lg relative',
            isCollapsed &&
              intercomOpen &&
              'bg-ds-brand-lightest border border-ds-brand-subtlest',
            isCollapsed && !intercomOpen && 'hover:bg-ds-gray-subtlest'
          )}
        >
          <ChatCircle
            size={16}
            weight="regular"
            className={
              intercomOpen ? 'text-ds-brand-default' : 'text-ds-text-subtle'
            }
          />
          {unreadCount > 0 && (
            <span className="absolute top-1 right-1 flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-ds-success-default opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-ds-success-default" />
            </span>
          )}
        </div>
      </div>
      <span
        className={cn(
          'flex-1 font-[500] leading-[18px] font-sf-pro-text text-sm text-left whitespace-nowrap overflow-hidden transition-[max-width,opacity] duration-200 ease-out',
          isCollapsed ? 'max-w-0 opacity-0' : 'max-w-[150px] opacity-100',
          intercomOpen ? 'text-ds-brand-default' : 'text-ds-text-subtle'
        )}
      >
        Message Support
      </span>
      {unreadCount > 0 && !isCollapsed && (
        <span className="shrink-0 mr-2 inline-flex items-center justify-center h-4 min-w-4 px-1 rounded-full bg-ds-success-default text-[10px] font-semibold text-white">
          {unreadCount}
        </span>
      )}
    </button>
  );
}

export function Sidebar({ className, navConfig }: SidebarProps) {
  const { isCollapsed, setIsCollapsed } = useSidebarCollapsed();
  const [showScrollHint, setShowScrollHint] = useState(false);

  useEffect(() => {
    try {
      if (localStorage.getItem(SCROLL_HINT_KEY) !== 'true') {
        setShowScrollHint(true);
        const dismissTimer = setTimeout(() => {
          setShowScrollHint(false);
          localStorage.setItem(SCROLL_HINT_KEY, 'true');
        }, 6000);
        return () => clearTimeout(dismissTimer);
      }
    } catch {}
  }, []);

  const dismissScrollHint = () => {
    if (!showScrollHint) return;
    setShowScrollHint(false);
    try {
      localStorage.setItem(SCROLL_HINT_KEY, 'true');
    } catch {}
  };

  const [clickedSection, setClickedSection] = useState<SidebarItemType | null>(
    null
  );
  const [promoDismissed, setPromoDismissed] = useState(false);
  const toggleHistory = useSideBarState((state) => state.toggleHistory);
  const { user, organization } = useUserLogged();
  const pathname = usePathname() || '/';

  // Google (identity-only) users get a reduced nav: Agent tabs first, then the
  // git-dependent Code tabs (rendered but gated), then Support. Used by both the
  // section filter and the group render order below.
  const isGoogleUser = user?.provider === 'google';

  // ENT-3531: when an admin hides the leaderboard org-wide, drop the nav item.
  // Treat the flag-loading window as hidden (same isLoading guard as the
  // /leaderboard route page): a hidden org must never flash the item on cold
  // load, and for visible orgs it only defers this one item until the cached
  // flags resolve (imperceptible; React Query serves subsequent nav from cache).
  const orgUuidForFlags = organization?.OrgUUID ?? user?.OrganizationUUID ?? '';
  const { isEnabled: isFeatureEnabled, isLoading: featureFlagsLoading } =
    useFeatureFlags(orgUuidForFlags);
  const leaderboardHidden =
    featureFlagsLoading || isFeatureEnabled(LEADERBOARD_HIDDEN_FLAG);

  // Trial seats. `locked` is the shared predicate TrialSeatGate uses, so the
  // nav and the gate cannot disagree about who is locked out. It stays false
  // while the status query is in flight and after any transport error, so a
  // slow or failed lookup never collapses someone's nav — the same fail-open
  // posture the backend seat gate takes.
  const { locked: trialSeatLocked } = useTrialAccessStatus(orgUuidForFlags);

  // Reset optimistic click highlight when pathname changes (back/forward,
  // external nav). React-recommended "reset state on prop change" pattern —
  // no effect, no extra render.
  const [lastPath, setLastPath] = useState(pathname);
  if (pathname !== lastPath) {
    setLastPath(pathname);
    setClickedSection(null);
  }

  // Derive active section from pathname; fall back to optimistic click
  // value during the brief window before route transition completes.
  const pathSection = useMemo(
    () => getActiveSectionFromPath(pathname),
    [pathname]
  );
  const sectionOpened = clickedSection ?? pathSection;

  // Auto-collapse sidebar on small screens
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1024) {
        // Collapse on screens smaller than 1024px (tablet and mobile)
        setIsCollapsed(true);
      }
    };

    // Check on mount
    handleResize();

    // Add event listener
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, [setIsCollapsed]);

  // Derive sections from navConfig or fallback. Render immediately on first paint
  // (don't gate on auth status) so non-role-gated items appear together with Ask Ellie.
  // Role-gated admin items will hydrate in once `user.Role` resolves.
  // NOTE: intercom subscription lives inside MessageSupportButton so updates
  // don't re-render the entire sidebar tree.
  const isEllieSidebarOpen = useAskEllieSidebarStore((s) => s.isOpen);

  // When Ellie sidebar is open, no page-level nav item should appear active —
  // ManagerChatSection handles its own active highlight independently.
  const effectiveSectionOpened = isEllieSidebarOpen ? null : sectionOpened;

  const filteredSections = useMemo(() => {
    const sections = (
      navConfig && navConfig.length > 0
        ? sectionsFromNavConfig(navConfig, user?.TeamUUID)
        : createNavigationSections(user?.TeamUUID)
    )
      // TODO: Boards hidden from sidebar for now — remove to restore.
      .filter((section) => section.type !== SidebarItemType.Boards);
    const roleFiltered = filterSectionsByRole(sections, user?.Role);
    const livesiteFiltered = roleFiltered
      .filter((section) => section.type !== SidebarItemType.Livesite)
      // Code Reliability hidden from sidebar for now — route remains at /code-reliability.
      .filter((section) => section.type !== SidebarItemType.CodeReliability);
    const orgFiltered = livesiteFiltered.filter((section) => {
      if (section.type === SidebarItemType.ModelQuality) {
        return isModelQualityEnabled(orgUuidForFlags);
      }
      if (section.type !== SidebarItemType.Reactors) {
        return true;
      }
      return isReactorsOrg(
        organization as unknown as {
          OrgUUID?: string;
          uuid?: string;
          orguuid?: string;
        }
      );
    });
    const leaderboardFiltered = leaderboardHidden
      ? orgFiltered.filter(
          (section) => section.type !== SidebarItemType.Leaderboard
        )
      : orgFiltered;

    // Google (identity-only) users have no SCM/git context. Keep the non-PR
    // product tabs (Agent Insights, Model Router, Model Quality) plus the three
    // Code tabs (Code Review, Code Reliability, Documentation) which render but
    // are gated behind an inline "connect your GitHub org" prompt, plus the
    // SUPPORT group (How to Use, CLI Guide, Talk to Expert, Message Support).
    // Home and the TEAM / DELIVERY groups are hidden. Group render order (Agents
    // before Code) is handled in the JSX below.
    const googleFiltered = isGoogleUser
      ? leaderboardFiltered.filter((section) => {
          const GOOGLE_KEEP_TYPES: SidebarItemType[] = [
            SidebarItemType.AgentInsights,
            SidebarItemType.ModelRouter,
            SidebarItemType.ModelQuality,
            SidebarItemType.CodeReview,
            SidebarItemType.CodeReliability,
            SidebarItemType.Documentation,
          ];
          return (
            GOOGLE_KEEP_TYPES.includes(section.type) ||
            section.group === 'SUPPORT'
          );
        })
      : leaderboardFiltered;

    // Trial seats: someone in a capped workspace who holds no seat keeps Home
    // and nothing else. Workspace Settings is unaffected — it lives in the
    // WorkspaceMenu dropdown at the bottom, not in these sections — so a
    // seatless person can still reach billing and their own account.
    //
    // trialSeatLocked stays false while the status query is in flight, so the
    // nav never flickers down to a single item for someone who does hold a seat.
    // Filtered from leaderboardFiltered, not googleFiltered, on purpose: the
    // Google allowlist above drops Home, so filtering the Google-filtered list
    // down to Home would leave a seatless Google user with an entirely empty
    // sidebar — and TrialSeatGate redirects them to /home, the one place they
    // could no longer navigate to.
    const seatFiltered = trialSeatLocked
      ? leaderboardFiltered.filter(
          (section) => section.type === SidebarItemType.Home
        )
      : googleFiltered;

    return sortByCanonicalOrder(seatFiltered);
  }, [
    user?.Role,
    user?.TeamUUID,
    isGoogleUser,
    navConfig,
    organization,
    orgUuidForFlags,
    leaderboardHidden,
    trialSeatLocked,
  ]);

  // Handle section click (for special cases only)
  const handleSectionClick = useCallback(
    async (section: SectionType) => {
      await handleSectionNavigation(section, toggleHistory, setClickedSection);
    },
    [toggleHistory]
  );

  // Group sections for rendering
  const groupedSections = useMemo(
    () => groupSectionsByType(filteredSections),
    [filteredSections]
  );

  // Split support sections once instead of three passes during render.
  const supportSections = useMemo(() => {
    const howToUse = groupedSections.support.filter(
      (s) => s.type === SidebarItemType.HowToUse
    );
    const cliGuide = groupedSections.support.filter(
      (s) => s.type === SidebarItemType.CliGuide
    );
    const hasBookACall = groupedSections.support.some(
      (s) => s.type === SidebarItemType.BookACall
    );
    const hasContactUs = groupedSections.support.some(
      (s) => s.type === SidebarItemType.ContactUs
    );
    return { howToUse, cliGuide, hasBookACall, hasContactUs };
  }, [groupedSections.support]);

  return (
    <TooltipProvider>
      <div
        className={cn(
          // Transparent on purpose: the shell paints bg-ds-surface-off-white +
          // the pixel-field texture behind this column, and z-10 lifts the nav
          // above that layer.
          'relative z-10 flex h-full flex-col overflow-hidden transition-[width] duration-200 ease-out group',
          isCollapsed ? 'w-16' : 'w-64',
          className
        )}
      >
        {/* Header — Entelligence brand + collapse */}
        <div className="shrink-0 pt-3 px-2">
          <div
            role={isCollapsed ? 'button' : undefined}
            tabIndex={isCollapsed ? 0 : undefined}
            onClick={isCollapsed ? () => setIsCollapsed(false) : undefined}
            onKeyDown={
              isCollapsed
                ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setIsCollapsed(false);
                    }
                  }
                : undefined
            }
            className="group/header flex items-center w-full rounded-lg h-10 overflow-hidden"
          >
            <div className="flex items-center justify-center shrink-0 w-12 h-10">
              <div
                className={cn(
                  'relative flex items-center justify-center size-6 rounded-md overflow-hidden transition-colors duration-150',
                  isCollapsed
                    ? 'bg-[#E7F2D4] border border-[#BDD29B] cursor-pointer group-hover/header:bg-transparent group-hover/header:border-transparent'
                    : 'bg-[#E7F2D4] border border-[#BDD29B]'
                )}
              >
                <Image
                  src="/assets/ent_logo_new_light_mode.svg"
                  alt="Entelligence"
                  width={20}
                  height={20}
                  className={cn(
                    'transition-opacity duration-150',
                    isCollapsed && 'group-hover/header:opacity-0'
                  )}
                />
                {isCollapsed && (
                  <span className="absolute inset-0 flex items-center justify-center text-ds-text-subtle opacity-0 transition-opacity duration-150 group-hover/header:opacity-100">
                    <PanelLeft className="h-5 w-5" />
                  </span>
                )}
              </div>
            </div>
            <span
              className={cn(
                'flex-1 font-[500] font-sf-pro-text text-sm text-ds-brand-default truncate whitespace-nowrap overflow-hidden transition-[max-width,opacity] duration-200 ease-out',
                isCollapsed ? 'max-w-0 opacity-0' : 'max-w-[160px] opacity-100'
              )}
            >
              Entelligence
            </span>
            <button
              type="button"
              aria-label="Collapse sidebar"
              onClick={(e) => {
                e.stopPropagation();
                setIsCollapsed(true);
              }}
              className={cn(
                'shrink-0 mr-2 size-6 flex items-center justify-center rounded text-ds-text-subtle hover:bg-ds-gray-subtlest overflow-hidden transition-[max-width,opacity] duration-200 ease-out',
                isCollapsed
                  ? 'max-w-0 opacity-0 mr-0'
                  : 'max-w-[24px] opacity-100'
              )}
            >
              <PanelLeft className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex-1 relative overflow-hidden min-h-0">
          {/* Top fade — nav items fade out as they scroll above */}
          <div
            aria-hidden
            className="pointer-events-none absolute top-0 left-0 right-0 h-4 z-10 bg-[linear-gradient(to_bottom,var(--ds-surface-off-white)_0%,transparent_100%)]"
          />
          <nav
            onScroll={dismissScrollHint}
            className={cn(
              'absolute inset-0 space-y-0.5 overflow-y-auto overflow-x-hidden pt-3 px-2 scrollbar-hide transition-[padding] duration-200',
              promoDismissed ? 'pb-[240px]' : 'pb-[380px]'
            )}
          >
            {/* General sections */}
            <div className="space-y-0.5">
              {groupedSections.general.map((section) => (
                <SidebarSectionButton
                  key={section.type}
                  section={section}
                  organizationUUID={user?.OrganizationUUID}
                  sectionOpened={effectiveSectionOpened}
                  isCollapsed={isCollapsed}
                  onSectionClick={handleSectionClick}
                  onSetSectionOpened={setClickedSection}
                />
              ))}
            </div>

            {/* Manager Chat Section - Ask Ellie */}
            <ManagerChatSection isCollapsed={isCollapsed} />

            {/* World Model - top level, next to Ask Ellie. Admin-only: this
                item lives outside the navConfig-driven `sections` array
                above, so filterSectionsByRole never sees it and the role
                check has to happen here instead. */}
            {isAdminOrOwner(user?.Role) && (
              <SidebarSectionButton
                section={WORLD_MODEL_SECTION}
                organizationUUID={user?.OrganizationUUID}
                sectionOpened={effectiveSectionOpened}
                isCollapsed={isCollapsed}
                onSectionClick={handleSectionClick}
                onSetSectionOpened={setClickedSection}
              />
            )}

            {/* Native activity is available to members and native setup to
                admins. The destination keeps other integrations Owner-only. */}
            {(isAdminOrOwner(user?.Role) || user?.Role === 'member') && (
              <SidebarSectionButton
                section={CONNECTORS_SECTION}
                organizationUUID={user?.OrganizationUUID}
                sectionOpened={effectiveSectionOpened}
                isCollapsed={isCollapsed}
                onSectionClick={handleSectionClick}
                onSetSectionOpened={setClickedSection}
              />
            )}

            {/* Code + Agents groups. Google users see Agents first (their primary
                tabs), then the git-gated Code tabs; everyone else keeps the
                default Code-then-Agents order. Both groups are always rendered,
                only their order flips. */}
            {(() => {
              const codeGroup = (
                <SidebarSectionGroup
                  key="code-group"
                  sections={groupedSections.code}
                  groupName="CODE"
                  isCollapsed={isCollapsed}
                  sectionOpened={effectiveSectionOpened}
                  organizationUUID={user?.OrganizationUUID}
                  onSectionClick={handleSectionClick}
                  onSetSectionOpened={setClickedSection}
                />
              );
              const agentsGroup = (
                <SidebarSectionGroup
                  key="agents-group"
                  sections={groupedSections.agents}
                  groupName="AGENTS"
                  isCollapsed={isCollapsed}
                  sectionOpened={effectiveSectionOpened}
                  organizationUUID={user?.OrganizationUUID}
                  onSectionClick={handleSectionClick}
                  onSetSectionOpened={setClickedSection}
                />
              );
              return isGoogleUser
                ? [agentsGroup, codeGroup]
                : [codeGroup, agentsGroup];
            })()}

            {/* Team sections — pb-8 keeps the last item (Automations) off the
                boundary with whatever follows, which was hard to read against it. */}
            <div className="pb-8">
              <SidebarSectionGroup
                sections={groupedSections.team}
                groupName="TEAM"
                isCollapsed={isCollapsed}
                sectionOpened={effectiveSectionOpened}
                organizationUUID={user?.OrganizationUUID}
                onSectionClick={handleSectionClick}
                onSetSectionOpened={setClickedSection}
              />
            </div>

            {/* Workflow sections */}
            <SidebarSectionGroup
              sections={groupedSections.workflow}
              groupName="DELIVERY"
              isCollapsed={isCollapsed}
              sectionOpened={effectiveSectionOpened}
              organizationUUID={user?.OrganizationUUID}
              onSectionClick={handleSectionClick}
              onSetSectionOpened={setClickedSection}
            />
          </nav>

          {/* First-visit hint — light gray down arrow above the pinned area */}
          {showScrollHint && !isCollapsed && (
            <div
              aria-hidden
              className={cn(
                'pointer-events-none absolute left-0 right-0 z-20 flex justify-center animate-bounce',
                promoDismissed ? 'bottom-[240px]' : 'bottom-[380px]'
              )}
            >
              <CaretDown
                size={18}
                weight="bold"
                className="text-ds-text-subtler opacity-60"
              />
            </div>
          )}

          {/* Pinned bottom area — floats above scroll; content scrolls beneath the gradient */}
          <div className="absolute left-0 right-0 bottom-0 px-2 pb-4 pt-12 space-y-2 pointer-events-none bg-[linear-gradient(to_bottom,transparent_0px,var(--ds-surface-off-white)_36px)]">
            <div className="pointer-events-auto space-y-2">
              <div
                className={cn(
                  'overflow-hidden transition-[max-height,opacity] ease-out',
                  isCollapsed
                    ? 'max-h-0 opacity-0 duration-150 delay-0'
                    : 'max-h-[400px] opacity-100 duration-200 delay-200'
                )}
              >
                <SidebarPromoCard
                  isCollapsed={false}
                  onDismissChange={setPromoDismissed}
                />
              </div>

              {/* Support — flat list, no heading (matches Figma) */}
              {groupedSections.support.length > 0 && (
                <div className="space-y-0.5">
                  {/* Documentation (external docs link) */}
                  {supportSections.howToUse.map((section) => (
                    <SidebarSectionButton
                      key={section.type}
                      section={section}
                      organizationUUID={user?.OrganizationUUID}
                      sectionOpened={effectiveSectionOpened}
                      isCollapsed={isCollapsed}
                      onSectionClick={handleSectionClick}
                      onSetSectionOpened={setClickedSection}
                    />
                  ))}

                  {/* CLI Guide (internal /cli manual page) */}
                  {supportSections.cliGuide.map((section) => (
                    <SidebarSectionButton
                      key={section.type}
                      section={section}
                      organizationUUID={user?.OrganizationUUID}
                      sectionOpened={effectiveSectionOpened}
                      isCollapsed={isCollapsed}
                      onSectionClick={handleSectionClick}
                      onSetSectionOpened={setClickedSection}
                    />
                  ))}

                  {/* Talk to Expert — fixed icon column, animated label slide */}
                  {supportSections.hasBookACall && (
                    <button
                      type="button"
                      aria-label={isCollapsed ? 'Talk to Expert' : undefined}
                      onClick={() => handleSectionClick(BOOK_A_CALL_SECTION)}
                      className={cn(
                        'flex items-center w-full h-9 rounded-lg overflow-hidden border border-transparent text-ds-text-subtle transition-colors duration-150',
                        !isCollapsed && 'hover:bg-ds-gray-subtlest'
                      )}
                    >
                      <div className="flex items-center justify-center shrink-0 w-12 h-9">
                        <div
                          className={cn(
                            'flex items-center justify-center size-9 rounded-lg',
                            isCollapsed && 'hover:bg-ds-gray-subtlest'
                          )}
                        >
                          <AsteriskSimple size={16} weight="regular" />
                        </div>
                      </div>
                      <span
                        className={cn(
                          'flex-1 font-[500] leading-[18px] font-sf-pro-text text-sm text-left whitespace-nowrap overflow-hidden transition-[max-width,opacity] duration-200 ease-out',
                          isCollapsed
                            ? 'max-w-0 opacity-0'
                            : 'max-w-[140px] opacity-100'
                        )}
                      >
                        Talk to Expert
                      </span>
                      <div
                        className={cn(
                          'shrink-0 size-[18px] rounded-full overflow-hidden border border-ds-border-subtlest transition-[max-width,opacity,margin] duration-200 ease-out',
                          isCollapsed
                            ? 'max-w-0 opacity-0 border-0 mr-0'
                            : 'max-w-[18px] opacity-100 mr-2'
                        )}
                      >
                        <Image
                          src="/assets/expert-avatar.png"
                          alt="Expert"
                          width={18}
                          height={18}
                          className="object-cover"
                        />
                      </div>
                    </button>
                  )}

                  {/* Message Support (Intercom) — isolated component so intercom
                  state updates don't re-render the entire sidebar tree. */}
                  {supportSections.hasContactUs && (
                    <MessageSupportButton
                      isCollapsed={isCollapsed}
                      onClick={() => handleSectionClick(CONTACT_US_SECTION)}
                    />
                  )}
                </div>
              )}

              {/* Workspace menu — workspace name + dropdown (settings, upgrade, logout) */}
              <div className="pt-2">
                <WorkspaceMenu isCollapsed={isCollapsed} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}
